import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SwitchHorizontalIcon, CubeIcon, ExclamationIcon } from '@heroicons/react/outline'
import { Button, DialogView } from '../../components'
import { PARTIAL_FULFILL_ERROR_FISCAL_BLOCK, PARTIAL_FULFILL_ERROR_MISSING_STOCK, PARTIAL_FULFILL_ERROR_WOULD_CUT_CONTAINER, PARTIAL_FULFILL_ERROR_NOT_SUPPORTED_FOR_CONTAINERS } from './partialFulfillErrors'
import { groupRemovedUnitsByDisposition } from './partialFulfillRemovedUnits'

/**
 * Partial fulfillment ("tarja" / Enviar parcial) dialog for ListProcessingOrders.
 * Presentational: the parent owns the flow state, the API call, and the handlers;
 * this component renders the per-state UI. Extracted from ListProcessingOrders to
 * keep that file focused and to isolate the fiscal-block handling (CUBBO-2461).
 *
 * @param {boolean} open
 * @param {'confirmation'|'loading'|'completion'|'error'} state
 * @param {object} order - the order being fulfilled (order_number, status).
 * @param {{items: Array, cancelOrder: boolean}} result - batch outcome.
 * @param {string} errorMessage - raw backend message (generic error path).
 * @param {Array} errorList - batch failure entries (generic error path).
 * @param {string|null} errorCode - canonical UI error code (see partialFulfillErrors).
 * @param {() => void} onCancel - full reset + close.
 * @param {() => void} onConfirm - confirm / retry the tarja.
 * @param {() => void} onGoToGenerateLabels - navigate to shipment generation.
 */
export const PartialFulfillDialog = ({
  open,
  state,
  order,
  result = { items: [], cancelOrder: false },
  errorMessage = '',
  errorList = [],
  errorCode = null,
  onCancel,
  onConfirm,
  onGoToGenerateLabels,
}) => {
  const { i18n } = useTranslation()

  // CUBBO-2461 FU-2: removed units have two dispositions — PICKED excess the
  // operator holds must be MOVED to Disgrace (an action) vs RESERVED not-found
  // units already registered as lost (informational, nothing to move). Split them
  // so the completion modal doesn't tell the operator to "move" what isn't there.
  const { toDisgrace: moveToDisgrace, lost: notFoundLost } = useMemo(
    () => groupRemovedUnitsByDisposition(result?.items),
    [result?.items],
  )

  const renderMoveRows = (rows, pillClassName) =>
    rows.map((row, idx) => (
      <div
        key={`${row.product?.id || ''}-${row.barcode}-${idx}`}
        className="bg-white p-3 rounded border text-left flex items-center justify-between"
      >
        <div>
          <div className="text-base font-semibold text-gray-900 truncate">UPC: {row.barcode}</div>
          <div className="text-sm text-gray-600">Nome: {row.product?.name}</div>
          <div className="text-sm text-gray-500">SKU: {row.sku}</div>
        </div>
        <span className={`ml-4 inline-flex items-center justify-center rounded-full text-xs font-semibold px-2 py-1 ${pillClassName}`}>
          x{row.count}
        </span>
      </div>
    ))

  const filteredErrorList = useMemo(() => {
    const headline = (errorMessage || '').trim()
    return (errorList || []).filter(err => {
      const msg = (err?.message || err?.error_class || '').trim()
      return msg && msg !== headline
    })
  }, [errorList, errorMessage])

  const isFiscalBlock = errorCode === PARTIAL_FULFILL_ERROR_FISCAL_BLOCK
  const isMissingStock = errorCode === PARTIAL_FULFILL_ERROR_MISSING_STOCK
  const wouldCutContainer = errorCode === PARTIAL_FULFILL_ERROR_WOULD_CUT_CONTAINER
  const containersNotSupported = errorCode === PARTIAL_FULFILL_ERROR_NOT_SUPPORTED_FOR_CONTAINERS

  // Allow Esc / backdrop dismissal in every state except `loading` — closing while
  // the batch runs would orphan an in-flight tarja. Dismissing is a full reset
  // (onCancel); after a successful tarja the list has already been refreshed by the
  // caller, so the row won't wrongly keep offering "Enviar parcial" (CUBBO-2461 J6).
  const dismissable = state !== 'loading'

  if (!open) return null

  return (
    <DialogView open={open} setOpen={() => dismissable && onCancel()} closeOnBackdrop={dismissable}>
      {state === 'confirmation' && (
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{i18n.t('list_processing_orders.partial_fulfillment.confirm_title_single')}</h3>
          <p className="text-gray-500 mb-6">{i18n.t('list_processing_orders.partial_fulfillment.confirm_description_single')}</p>
          <div className="flex flex-wrap gap-2 justify-center mb-6">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">#{order?.order_number}</span>
          </div>
          <div className="flex gap-3 justify-center">
            <Button type="secondary" onClick={onCancel}>
              {i18n.t('list_processing_orders.partial_fulfillment.cancel_label')}
            </Button>
            <Button onClick={onConfirm}>{i18n.t('list_processing_orders.partial_fulfillment.confirm_label')}</Button>
          </div>
        </div>
      )}

      {state === 'loading' && (
        <div className="text-center">
          <div className="mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              viewBox="0 0 100 100"
              preserveAspectRatio="xMidYMid"
            >
              <g transform="translate(50 50) scale(0.7) translate(-50 -50)">
                <g>
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    repeatCount="indefinite"
                    calcMode="spline"
                    dur="4s"
                    values="0 50 50;90 50 50;180 50 50;270 50 50;360 50 50"
                    keyTimes="0;0.25;0.5;0.75;1"
                    keySplines="0 1 0 1;0 1 0 1;0 1 0 1;0 1 0 1"
                  ></animateTransform>
                  <g>
                    <animateTransform
                      attributeName="transform"
                      type="scale"
                      dur="1s"
                      repeatCount="indefinite"
                      calcMode="spline"
                      values="1;1;0.5"
                      keyTimes="0;0.5;1"
                      keySplines="1 0 0 1;1 0 0 1"
                    ></animateTransform>
                    <g transform="translate(25 25)">
                      <rect x="-25" y="-25" width="52" height="52" fill="#182731">
                        <animate
                          attributeName="fill"
                          dur="4s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          values="#182731;#a7d4ec;#182731;#a7d4ec;#182731"
                          keyTimes="0;0.25;0.5;0.75;1"
                          keySplines="0 1 0 1;0 1 0 1;0 1 0 1;0 1 0 1"
                        ></animate>
                      </rect>
                    </g>
                    <g transform="translate(25 75)">
                      <rect x="-25" y="-25" width="52" height="50" fill="#182731">
                        <animateTransform
                          attributeName="transform"
                          type="scale"
                          dur="1s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          values="0;1;1"
                          keyTimes="0;0.5;1"
                          keySplines="1 0 0 1;1 0 0 1"
                        ></animateTransform>
                        <animate
                          attributeName="fill"
                          dur="4s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          values="#182731;#a7d4ec;#182731;#a7d4ec;#182731"
                          keyTimes="0;0.25;0.5;0.75;1"
                          keySplines="0 1 0 1;0 1 0 1;0 1 0 1;0 1 0 1"
                        ></animate>
                      </rect>
                    </g>
                    <g transform="translate(75 25)">
                      <rect x="-25" y="-25" width="50" height="52" fill="#182731">
                        <animateTransform
                          attributeName="transform"
                          type="scale"
                          dur="1s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          values="0;1;1"
                          keyTimes="0;0.5;1"
                          keySplines="1 0 0 1;1 0 0 1"
                        ></animateTransform>
                        <animate
                          attributeName="fill"
                          dur="4s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          values="#182731;#a7d4ec;#182731;#a7d4ec;#182731"
                          keyTimes="0;0.25;0.5;0.75;1"
                          keySplines="0 1 0 1;0 1 0 1;0 1 0 1;0 1 0 1"
                        ></animate>
                      </rect>
                    </g>
                    <g transform="translate(75 75)">
                      <rect x="-25" y="-25" width="50" height="50" fill="#182731">
                        <animateTransform
                          attributeName="transform"
                          type="scale"
                          dur="1s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          values="0;1;1"
                          keyTimes="0;0.5;1"
                          keySplines="1 0 0 1;1 0 0 1"
                        ></animateTransform>
                        <animate
                          attributeName="fill"
                          dur="4s"
                          repeatCount="indefinite"
                          calcMode="spline"
                          values="#182731;#a7d4ec;#182731;#a7d4ec;#182731"
                          keyTimes="0;0.25;0.5;0.75;1"
                          keySplines="0 1 0 1;0 1 0 1;0 1 0 1;0 1 0 1"
                        ></animate>
                      </rect>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{i18n.t('list_processing_orders.partial_fulfillment.loading_title')}</h3>
          <p className="text-gray-500">{i18n.t('list_processing_orders.partial_fulfillment.loading_description')}</p>
        </div>
      )}

      {state === 'completion' && (
        <div className="text-center">
          {result.cancelOrder ? (
            <>
              <div className="mb-4 flex items-center justify-center">
                <ExclamationIcon className="w-16 h-16 text-yellow-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{i18n.t('list_processing_orders.partial_fulfillment.dialog_completion_title')}</h3>
              <p className="text-gray-500 mb-6">{i18n.t('list_processing_orders.partial_fulfillment.dialog_completion_description_cancel')}</p>
              <div className="flex justify-center gap-3">
                <Button onClick={onCancel}>{i18n.t('list_processing_orders.partial_fulfillment.dialog_ok_label')}</Button>
              </div>
            </>
          ) : Array.isArray(result.items) && result.items.length === 0 ? (
            <div>
              <div className="mb-4 flex items-center justify-center">
                <CubeIcon className="w-16 h-16 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">{i18n.t('list_processing_orders.partial_fulfillment.dialog_completion_title')}</h3>
              <p className="text-gray-600">
                {i18n.t('list_processing_orders.partial_fulfillment.dialog_completion_description_ready')} #{order?.order_number}
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Button type="secondary" onClick={onCancel}>
                  {i18n.t('list_processing_orders.partial_fulfillment.close_label')}
                </Button>
                <Button onClick={onGoToGenerateLabels}>{i18n.t('list_processing_orders.actions.generate_shipment')}</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-center justify-center">
                <SwitchHorizontalIcon className="w-16 h-16 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{i18n.t('list_processing_orders.partial_fulfillment.completion_title')}</h3>
              {moveToDisgrace.length > 0 && (
                <div className="bg-gray-100 rounded-lg p-4 mb-4 text-left">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">{i18n.t('list_processing_orders.partial_fulfillment.move_to_disgrace_title')}</h4>
                  <p className="text-xs text-gray-500 mb-3">{i18n.t('list_processing_orders.partial_fulfillment.move_to_disgrace_description')}</p>
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                    {renderMoveRows(moveToDisgrace, 'bg-blue-50 text-blue-700')}
                  </div>
                </div>
              )}
              {notFoundLost.length > 0 && (
                <div className="bg-gray-100 rounded-lg p-4 mb-6 text-left">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">{i18n.t('list_processing_orders.partial_fulfillment.not_found_lost_title')}</h4>
                  <p className="text-xs text-gray-500 mb-3">{i18n.t('list_processing_orders.partial_fulfillment.not_found_lost_description')}</p>
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                    {renderMoveRows(notFoundLost, 'bg-gray-200 text-gray-600')}
                  </div>
                </div>
              )}
              <div className="flex justify-center gap-3">
                <Button type="secondary" onClick={onCancel}>
                  {i18n.t('list_processing_orders.partial_fulfillment.close_label')}
                </Button>
                <Button onClick={onGoToGenerateLabels}>{i18n.t('list_processing_orders.actions.generate_shipment')}</Button>
              </div>
            </>
          )}
        </div>
      )}

      {state === 'error' &&
        (isMissingStock ? (
          <div className="text-center">
            <div className="mb-4 flex items-center justify-center">
              <ExclamationIcon className="w-16 h-16 text-yellow-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{i18n.t('list_processing_orders.partial_fulfillment.error_missing_stock_title')}</h3>
            <div className="mb-4 flex flex-wrap gap-2 justify-center">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">#{order?.order_number}</span>
            </div>
            <p className="text-gray-600 mb-6">{i18n.t('list_processing_orders.partial_fulfillment.error_missing_stock_description')}</p>
            <div className="flex justify-center">
              <Button onClick={onCancel}>{i18n.t('list_processing_orders.partial_fulfillment.dialog_ok_label')}</Button>
            </div>
          </div>
        ) : isFiscalBlock ? (
          <div className="text-center">
            <div className="mb-4 flex items-center justify-center">
              <ExclamationIcon className="w-16 h-16 text-yellow-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{i18n.t('list_processing_orders.partial_fulfillment.error_fiscal_block_title')}</h3>
            <div className="mb-4 flex flex-wrap gap-2 justify-center">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">#{order?.order_number}</span>
            </div>
            <p className="text-gray-600 mb-6">{i18n.t('list_processing_orders.partial_fulfillment.error_fiscal_block_description')}</p>
            <div className="flex justify-center">
              <Button onClick={onCancel}>{i18n.t('list_processing_orders.partial_fulfillment.dialog_ok_label')}</Button>
            </div>
          </div>
        ) : wouldCutContainer ? (
          <div className="text-center">
            <div className="mb-4 flex items-center justify-center">
              <ExclamationIcon className="w-16 h-16 text-yellow-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{i18n.t('list_processing_orders.partial_fulfillment.error_would_cut_container_title')}</h3>
            <div className="mb-4 flex flex-wrap gap-2 justify-center">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">#{order?.order_number}</span>
            </div>
            <p className="text-gray-600 mb-6">{i18n.t('list_processing_orders.partial_fulfillment.error_would_cut_container_description')}</p>
            <div className="flex justify-center">
              <Button onClick={onCancel}>{i18n.t('list_processing_orders.partial_fulfillment.dialog_ok_label')}</Button>
            </div>
          </div>
        ) : containersNotSupported ? (
          <div className="text-center">
            <div className="mb-4 flex items-center justify-center">
              <ExclamationIcon className="w-16 h-16 text-yellow-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{i18n.t('list_processing_orders.partial_fulfillment.error_containers_not_supported_title')}</h3>
            <div className="mb-4 flex flex-wrap gap-2 justify-center">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">#{order?.order_number}</span>
            </div>
            <p className="text-gray-600 mb-6">{i18n.t('list_processing_orders.partial_fulfillment.error_containers_not_supported_description')}</p>
            <div className="flex justify-center">
              <Button onClick={onCancel}>{i18n.t('list_processing_orders.partial_fulfillment.dialog_ok_label')}</Button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{i18n.t('list_processing_orders.partial_fulfillment.dialog_error_title')}</h3>
            <p className="text-gray-500">{i18n.t('list_processing_orders.partial_fulfillment.dialog_error_description')}</p>
            {(filteredErrorList.length > 0 || errorMessage) && (
              <div className="mb-6 text-center">
                {errorMessage && <p className="text-sm text-red-600 break-all">{errorMessage}</p>}
                {filteredErrorList.length > 0 && (
                  <ul className="mt-2 list-none space-y-1">
                    {filteredErrorList.map((err, idx) => (
                      <li key={idx} className="text-sm text-red-600 break-all">
                        {err?.message || err?.error_class || JSON.stringify(err)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Button type="secondary" onClick={onCancel}>
                {i18n.t('list_processing_orders.partial_fulfillment.dialog_cancel_label')}
              </Button>
              <Button onClick={onConfirm}>{i18n.t('list_processing_orders.partial_fulfillment.dialog_retry_label')}</Button>
            </div>
          </div>
        ))}
    </DialogView>
  )
}
